import { ComparisonValidityState } from "../../declarations/types";
import type { IComparisonInfo } from "../../declarations/types";
import type { ExplorerNodeStatus } from "../../declarations/IExplorerTypes";
import type { IRouteOperationD365 } from "../../hooks/Route/useGetRouteOperationsByRouteD365";
import type { IRouteOperationPropertyD365 } from "../../hooks/Route/useGetRouteOperationPropertiesByRouteD365";
import type { IRouteOperationRequirementD365 } from "../../hooks/Route/useGetRouteOperationRequirementsByRouteD365";
import type { IBomLineD365 } from "../../hooks/Route/useGetBomLinesByBomD365";

/**
 * Module-scoped empty arrays. Used as stable fallback references when query data
 * is undefined — prevents `?? []` from creating a new empty array on every render,
 * which would otherwise cascade through the d365BomLineMap → useEffect → version
 * counter → d365FallbackRPKeys cycle in the sales/manufacturing graphs and trigger
 * an infinite re-render loop. Importing the same module-level constants from both
 * hooks preserves referential equality across them.
 */
export const EMPTY_OPERATIONS_D365: IRouteOperationD365[] = [];
export const EMPTY_OP_PROPERTIES_D365: IRouteOperationPropertyD365[] = [];
export const EMPTY_OP_REQUIREMENTS_D365: IRouteOperationRequirementD365[] = [];
export const EMPTY_BOM_LINES_D365: IBomLineD365[] = [];

/** Subset of `UseQueryResult` we project comparison-query results into. */
export interface IComparisonQueryResult<TData> {
    data?: TData;
    isLoading: boolean;
    isError: boolean;
    isFetched: boolean;
}

/** Subset of `UseQueryResult` we project D365 entity-fetch query results into. */
export interface ID365QueryResult<TData> {
    data?: TData;
    isLoading: boolean;
    isError: boolean;
}

interface IComparisonQuerySource<TData> {
    data?: TData;
    isLoading: boolean;
    isError: boolean;
    isFetched: boolean;
}

interface ID365QuerySource<TData> {
    data?: TData;
    isLoading: boolean;
    isError: boolean;
}

/**
 * Build a positional `keys` ↔ `queries` lookup map keyed by stringification of each key.
 * Use this for comparison-info queries (CS↔D365 diff endpoints) where downstream code needs
 * `isFetched` to distinguish "never loaded" from "loaded with no data".
 */
export function buildComparisonQueryMap<TKey, TData>(
    keys: readonly TKey[],
    queries: readonly IComparisonQuerySource<TData>[],
    keyOf: (key: TKey) => string,
): Map<string, IComparisonQueryResult<TData>> {
    const map = new Map<string, IComparisonQueryResult<TData>>();
    keys.forEach((key, index) => {
        const q = queries[index];
        if (q) {
            map.set(keyOf(key), {
                data: q.data,
                isLoading: q.isLoading,
                isError: q.isError,
                isFetched: q.isFetched,
            });
        }
    });
    return map;
}

/**
 * Build a positional `keys` ↔ `queries` lookup map keyed by stringification of each key.
 * Use this for D365 entity-fetch queries that don't need `isFetched`.
 */
export function buildD365QueryMap<TKey, TData>(
    keys: readonly TKey[],
    queries: readonly ID365QuerySource<TData>[],
    keyOf: (key: TKey) => string,
): Map<string, ID365QueryResult<TData>> {
    const map = new Map<string, ID365QueryResult<TData>>();
    keys.forEach((key, index) => {
        const q = queries[index];
        if (q) {
            map.set(keyOf(key), {
                data: q.data,
                isLoading: q.isLoading,
                isError: q.isError,
            });
        }
    });
    return map;
}

/**
 * Reduce a comparison query result into the node-level status used to colour explorer nodes.
 * Identical logic shared by sales/service/manufacturing graph hooks.
 */
export function computeStatus(
    data: IComparisonInfo[] | undefined,
    isLoading: boolean,
    isError: boolean,
    isFetched: boolean,
): { status: ExplorerNodeStatus; discrepancyCount: number; totalFields: number } {
    if (isLoading) {
        return { status: "loading", discrepancyCount: 0, totalFields: 0 };
    }
    if (isError) {
        return { status: "error", discrepancyCount: 0, totalFields: 0 };
    }
    if (!isFetched || !data) {
        return { status: "na", discrepancyCount: 0, totalFields: 0 };
    }
    const total = data.length;
    const invalid = data.filter(
        (d) => d.validity === ComparisonValidityState.Invalid,
    ).length;
    const warnings = data.filter(
        (d) => d.validity === ComparisonValidityState.Warning,
    ).length;

    if (invalid > 0) {
        return { status: "discrepancies", discrepancyCount: invalid + warnings, totalFields: total };
    }
    if (warnings > 0) {
        return { status: "discrepancies", discrepancyCount: warnings, totalFields: total };
    }
    return { status: "synced", discrepancyCount: 0, totalFields: total };
}

/**
 * Returns true when the supplied tax-exempt number is non-blank after stripping whitespace
 * and dashes. Used to gate VAT-comparison fetches in sales/service graph hooks.
 */
export function isValidTaxExemptNumber(value: string | undefined | null): boolean {
    if (!value) {
        return false;
    }
    return value.replace(/[\s-]/g, "").length > 0;
}
